import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Caption, SectionHeader } from "../../components/ui/ds";
import { fmtMetric, fmtShortDate, todayKey } from "../../lib/format";
import { summarizeMetric } from "../../lib/metric-stats";
import "./metric-stats.css";

// MERATEĽNÝ NÁVYK („vlastný výsledok") — štatistiky v detaile návyku.
//
// Zadanie majiteľky: „keď mám návyk s vlastným výsledkom, chcem niekde vidieť štatistiky".
// Odškrtávací návyk má sériu a percentá; merateľný má naviac ČÍSLA (koľko strán, minút,
// litrov) — a tie sa doteraz nikde nedali pozrieť: hodnota sa zapísala a zmizla.
//
// Čo tu je: stĺpcový graf posledných 30 dní (plný stĺpec = deň, keď bol cieľ splnený)
// a štyri čísla — priemer, rekord, spolu (VŠETKO za celý čas) a tento týždeň s porovnaním
// voči minulému. Dáta ťahá `GET /api/habit-metric-stats` až pri otvorení detailu, rovnako
// ako kolá (do /api/state nepatria — to sa poluje každých 45 s).
//
// Priemer a rekord počítajú len DNI SO ZÁPISOM — návyk „3× do týždňa" by inak vyzeral,
// že v priemere skoro nič nerobíš. Čiara cieľa sa kreslí len pri dennom cieli: pri
// „N× za obdobie" sa pokus prenáša cez polnoc, takže denná čiara by klamala.
//
// Ten istý pohľad (MetricStatsView) používa aj SÚŤAŽ — tam sa čísla rátajú z `my_days`
// priamo v prehliadači (CompetitionStats), aby dizajn „Tvoje čísla" bol na oboch miestach rovnaký.
export function MetricStats({ task }) {
  const [d, setD] = useState(undefined);   // undefined = načítava sa, null = nepodarilo sa

  useEffect(() => {
    let alive = true;
    setD(undefined);
    api("/api/habit-metric-stats?task_id=" + task.id)
      .then((r) => { if (alive) setD(r); })
      .catch(() => { if (alive) setD(null); });
    return () => { alive = false; };
  }, [task.id]);

  if (d === undefined || d === null) return null;   // ticho — detail funguje aj bez čísel

  const target = Number(d.target) || 0;
  const dayGoal = target > 0 && !d.period;          // cieľ platí na DEŇ (nie na prenášaný pokus)
  return (
    <MetricStatsView d={d} unit={d.unit || ""} isTime={!!d.is_time} target={dayGoal ? target : 0}
      axis={[`pred ${d.window_days} dňami`, "dnes"]}
      note={dayGoal
        ? `Cieľ ${fmtMetric(target, d.unit || "", !!d.is_time)}: splnený v ${d.done_window} z ${d.window_days} dní. Priemer a rekord počítajú len dni so zápisom.`
        : "Priemer a rekord počítajú len dni, v ktorých máš zápis."} />
  );
}

// SÚŤAŽ — moje čísla z `my_days` (detail súťaže ich má vždy, nič sa nedoťahuje).
// Okno končí dneškom, alebo dňom konca súťaže, keď už skončila; nezačína pred štartom.
// Cieľ na deň súťaž nemá, takže každý stĺpec so zápisom je „plný".
export function CompetitionStats({ data, unit, isTime }) {
  const today = todayKey();
  const last = data.end_day && data.end_day < today ? data.end_day : today;
  const d = summarizeMetric(data.my_days || {}, last, { firstDay: data.start_day });
  return (
    <MetricStatsView d={d} unit={unit} isTime={isTime}
      axis={[fmtShortDate(d.first_window_day), last === today ? "dnes" : fmtShortDate(last)]}
      note="Priemer a rekord počítajú len dni, v ktorých máš zápis." />
  );
}

// Spoločný pohľad: hlavička „Tvoje čísla", graf, štyri dlaždice, poznámka.
// d = {days[], window_days, total, logged, avg, best, week, week_prev} · target > 0 kreslí čiaru cieľa.
export function MetricStatsView({ d, unit = "", isTime = false, target = 0, axis, note }) {
  const val = (v) => fmtMetric(v, unit, isTime);
  const days = d.days || [];
  const peak = Math.max(target > 0 ? target : 0, ...days.map((x) => x.amount || 0), 1);
  const delta = d.week_prev > 0 ? Math.round(((d.week - d.week_prev) / d.week_prev) * 100) : null;

  return (
    <>
      <SectionHeader label="Tvoje čísla" icon="chart" />
      {!d.logged ? (
        <Caption variant="note">Zatiaľ žiadny zápis — čísla sa tu objavia hneď po prvom.</Caption>
      ) : (<>
        <div className="bz-mstat__chart" aria-hidden="true">
          {target > 0 && <span className="bz-mstat__goal" style={{ bottom: `${(target / peak) * 100}%` }} />}
          {days.map((x) => (
            <span key={x.day} className="bz-mstat__col"
              title={`${fmtShortDate(x.day)} ${val(x.amount)}`}>
              {x.amount > 0 && (
                <span className={"bz-mstat__bar" + (x.done ? " bz-mstat__bar--done" : "")}
                  style={{ height: `${Math.max(4, (x.amount / peak) * 100)}%` }} />
              )}
            </span>
          ))}
        </div>
        <div className="bz-mstat__axis">
          <span>{axis[0]}</span>
          <span>{axis[1]}</span>
        </div>
        <div className="bz-mstat__grid">
          <div className="bz-mstat__cell">
            <span className="bz-mstat__val">{val(d.avg)}</span>
            <span className="bz-mstat__lbl">priemer na deň</span>
          </div>
          <div className="bz-mstat__cell">
            <span className="bz-mstat__val">{val(d.best ? d.best.amount : 0)}</span>
            <span className="bz-mstat__lbl">{d.best ? `rekord · ${fmtShortDate(d.best.day)}` : "rekord"}</span>
          </div>
          <div className="bz-mstat__cell">
            <span className="bz-mstat__val">{val(d.total)}</span>
            <span className="bz-mstat__lbl">spolu za {d.logged} {d.logged === 1 ? "deň" : (d.logged >= 2 && d.logged <= 4 ? "dni" : "dní")}</span>
          </div>
          <div className="bz-mstat__cell">
            <span className="bz-mstat__val">{val(d.week)}
              {delta !== null && delta !== 0 && (
                <span className={"bz-mstat__delta" + (delta > 0 ? " bz-mstat__delta--up" : "")}>
                  {delta > 0 ? "+" : "−"}{Math.abs(delta)} %
                </span>
              )}
            </span>
            <span className="bz-mstat__lbl">tento týždeň</span>
          </div>
        </div>
        {note && <Caption variant="note">{note}</Caption>}
      </>)}
    </>
  );
}
